// Settings — full screen. Post-auth: the Caretakers list is the real
// `baby_caretakers` roster; sharing + delete are creator-only.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Baby, BabyFeedDefaults, CaretakerRole } from "../routes/BabyShell";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import { lastUsedBabyId } from "../lib/babyId";
import { ageFromBirthday, formatAge } from "../lib/age";
import { BabyAvatar } from "./BabyAvatar";
import { DeleteConfirm } from "./DeleteConfirm";
import { ShareSheet } from "./ShareSheet";
import { useAmount, useLocale } from "../lib/LocaleContext";

type Props = {
  baby: Baby;
  role: CaretakerRole;
  onPhotoChange: (dataUrl: string) => Promise<void> | void;
  onNameChange: (name: string) => Promise<void> | void;
  onDefaultsChange: (fields: Partial<BabyFeedDefaults>) => Promise<void> | void;
  onDelete: () => Promise<void>;
};

type CaretakerRow = { user_id: string; role: string; display_name: string; created_at: string };

export function SettingsView({ baby, role, onPhotoChange, onNameChange, onDefaultsChange, onDelete }: Props) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { locale, setLocale, unit, setUnit, t } = useLocale();
  const amount = useAmount();
  const isCreator = role === "creator";

  const [signingOut, setSigningOut] = useState(false);
  const [signOutFailed, setSignOutFailed] = useState(false);

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutFailed(false);
    try {
      await signOut();
    } catch {
      setSignOutFailed(true);
      setSigningOut(false);
    }
  }

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [caretakers, setCaretakers] = useState<CaretakerRow[]>([]);
  const [myName, setMyName] = useState("");
  const [nameSaved, setNameSaved] = useState(false);
  const [babyName, setBabyName] = useState(baby.name);
  const [babyNameSaved, setBabyNameSaved] = useState(false);
  const [leaveArmed, setLeaveArmed] = useState(false);

  // Feed defaults, edited in the active display unit (storage is always oz).
  const [bottleText, setBottleText] = useState(() =>
    baby.bottle_default_oz != null ? String(amount.value(baby.bottle_default_oz)) : "",
  );
  const [nurseText, setNurseText] = useState(() =>
    baby.nurse_default_oz != null ? String(amount.value(baby.nurse_default_oz)) : "",
  );
  const [bottleSaved, setBottleSaved] = useState(false);
  const [nurseSaved, setNurseSaved] = useState(false);

  // Keep the editable fields in sync if the baby reloads (e.g. after a failed save refetch).
  useEffect(() => {
    setBabyName(baby.name);
  }, [baby.name]);
  useEffect(() => {
    setBottleText(baby.bottle_default_oz != null ? String(amount.value(baby.bottle_default_oz)) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby.bottle_default_oz, unit]);
  useEffect(() => {
    setNurseText(baby.nurse_default_oz != null ? String(amount.value(baby.nurse_default_oz)) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby.nurse_default_oz, unit]);

  async function loadCaretakers() {
    const { data } = await supabase
      .from("baby_caretakers")
      .select("user_id, role, display_name, created_at")
      .eq("baby_id", baby.id)
      .order("created_at", { ascending: true });
    setCaretakers(data ?? []);
    const mine = (data ?? []).find((c) => c.user_id === user?.id);
    if (mine) setMyName(mine.display_name);
  }

  useEffect(() => {
    loadCaretakers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby.id, user?.id]);

  async function saveMyName() {
    const n = myName.trim();
    if (!n) return;
    await supabase.from("baby_caretakers").update({ display_name: n }).eq("baby_id", baby.id).eq("user_id", user?.id ?? "");
    setNameSaved(true);
    setTimeout(() => setNameSaved(false), 2000);
    loadCaretakers();
  }

  async function saveBabyName() {
    const n = babyName.trim();
    if (!n || n === baby.name) return;
    await onNameChange(n);
    setBabyNameSaved(true);
    setTimeout(() => setBabyNameSaved(false), 2000);
  }

  // Empty input = clear the default (quick chips fall back to the last logged value).
  const bottleOz = bottleText.trim() === "" ? null : Math.round(amount.toOz(Number(bottleText)) * 100) / 100;
  const bottleValid = bottleOz === null || (Number.isFinite(bottleOz) && bottleOz > 0 && bottleOz <= 20);
  const nurseOz = nurseText.trim() === "" ? null : Math.round(amount.toOz(Number(nurseText)) * 100) / 100;
  const nurseValid = nurseOz === null || (Number.isFinite(nurseOz) && nurseOz > 0 && nurseOz <= 20);

  async function saveBottleDefault() {
    if (!bottleValid) return;
    await onDefaultsChange({ bottle_default_oz: bottleOz });
    setBottleSaved(true);
    setTimeout(() => setBottleSaved(false), 2000);
  }

  async function saveNurseDefault() {
    if (!nurseValid) return;
    await onDefaultsChange({ nurse_default_oz: nurseOz });
    setNurseSaved(true);
    setTimeout(() => setNurseSaved(false), 2000);
  }

  async function removeCaretaker(uid: string) {
    await supabase.from("baby_caretakers").delete().eq("baby_id", baby.id).eq("user_id", uid);
    loadCaretakers();
  }

  async function leaveTracker() {
    if (!leaveArmed) {
      setLeaveArmed(true);
      return;
    }
    await supabase.from("baby_caretakers").delete().eq("baby_id", baby.id).eq("user_id", user?.id ?? "");
    lastUsedBabyId.clear(user?.id);
    navigate("/", { replace: true });
  }

  return (
    <div className="bh-root">
      <div style={styles.head}>
        <button style={styles.back} onClick={() => navigate(-1)} aria-label={t.settings.back}>
          ←
        </button>
        <div style={styles.title}>{t.settings.title}</div>
        <div style={{ width: 44 }} />
      </div>

      <div className="bh-scroll" style={{ paddingBottom: 60 }}>
        <Section label={t.settings.babySection}>
          <Row label={t.settings.photo}>
            <BabyAvatar size={64} name={baby.name} photoDataUrl={baby.photo_data} editable onPhotoChange={onPhotoChange} />
          </Row>
          {isCreator ? (
            <div style={styles.row}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.rowLabel}>{t.settings.name}</div>
                <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                  <input
                    style={styles.nameInput}
                    value={babyName}
                    onChange={(e) => setBabyName(e.target.value)}
                    placeholder={t.settings.babyNamePlaceholder}
                  />
                  <button
                    style={styles.saveBtn}
                    onClick={saveBabyName}
                    disabled={!babyName.trim() || babyName.trim() === baby.name}
                  >
                    {babyNameSaved ? t.settings.saved : t.settings.save}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <Row label={t.settings.name} value={baby.name} />
          )}
          <Row label={t.settings.birthday} value={baby.birthday} sub={formatAge(ageFromBirthday(baby.due_date ?? baby.birthday))} />
          {baby.due_date && <Row label={t.settings.dueDate} value={baby.due_date} />}
        </Section>

        <Section label={t.settings.feedingSection}>
          {isCreator ? (
            <>
              <div style={styles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowLabel}>{t.settings.bottleDefaultLabel(amount.word)}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <input
                      style={styles.nameInput}
                      type="number"
                      inputMode="decimal"
                      step={amount.unit === "ml" ? "5" : "0.5"}
                      min="0"
                      value={bottleText}
                      onChange={(e) => setBottleText(e.target.value)}
                      placeholder={t.settings.notSet}
                    />
                    <button style={styles.saveBtn} onClick={saveBottleDefault} disabled={!bottleValid}>
                      {bottleSaved ? t.settings.saved : t.settings.save}
                    </button>
                  </div>
                </div>
              </div>
              <div style={styles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowLabel}>{t.settings.nurseDefaultLabel(amount.word)}</div>
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <input
                      style={styles.nameInput}
                      type="number"
                      inputMode="decimal"
                      step={amount.unit === "ml" ? "5" : "0.5"}
                      min="0"
                      value={nurseText}
                      onChange={(e) => setNurseText(e.target.value)}
                      placeholder={t.settings.notSet}
                    />
                    <button style={styles.saveBtn} onClick={saveNurseDefault} disabled={!nurseValid}>
                      {nurseSaved ? t.settings.saved : t.settings.save}
                    </button>
                  </div>
                </div>
              </div>
              <p style={styles.helper}>{t.settings.feedingHelper}</p>
            </>
          ) : (
            <>
              <Row
                label={t.settings.bottleDefaultLabel(amount.word)}
                value={baby.bottle_default_oz != null ? amount.fmt(baby.bottle_default_oz) : t.settings.notSet}
              />
              <Row
                label={t.settings.nurseDefaultLabel(amount.word)}
                value={baby.nurse_default_oz != null ? amount.fmt(baby.nurse_default_oz) : t.settings.notSet}
              />
            </>
          )}
        </Section>

        <Section label={t.settings.youSection}>
          <div style={styles.nameRow}>
            <input
              style={styles.nameInput}
              value={myName}
              onChange={(e) => setMyName(e.target.value)}
              placeholder={t.settings.yourNamePlaceholder}
            />
            <button style={styles.saveBtn} onClick={saveMyName} disabled={!myName.trim()}>
              {nameSaved ? t.settings.saved : t.settings.save}
            </button>
          </div>
          <p style={styles.helper}>
            {isCreator ? t.settings.youAreCreator(baby.name) : t.settings.youAreCaretaker(baby.name)}
          </p>
        </Section>

        <Section label={t.settings.caretakersSection}>
          {caretakers.length === 0 ? (
            <div style={styles.helper}>{t.settings.loading}</div>
          ) : (
            caretakers.map((c) => (
              <div key={c.user_id} style={styles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.rowValue}>
                    {c.display_name}
                    {c.user_id === user?.id && <span style={styles.youTag}> {t.settings.youTag}</span>}
                  </div>
                  <div style={styles.rowSub}>{c.role === "creator" ? t.settings.roleCreator : t.settings.roleCaretaker}</div>
                </div>
                {isCreator && c.user_id !== user?.id && (
                  <button style={styles.removeBtn} onClick={() => removeCaretaker(c.user_id)}>
                    {t.settings.remove}
                  </button>
                )}
              </div>
            ))
          )}
        </Section>

        {isCreator && (
          <Section label={t.settings.sharingSection}>
            <button style={styles.shareBtn} onClick={() => setShareOpen(true)}>
              {t.settings.inviteCaretaker}
            </button>
            <p style={styles.helper}>{t.settings.inviteHelper}</p>
          </Section>
        )}

        <Section label={t.settings.displaySection}>
          <Row label={t.settings.milkUnits}>
            <div style={styles.langToggle}>
              {(["oz", "ml"] as const).map((u) => (
                <button
                  key={u}
                  style={u === unit ? styles.langOptActive : styles.langOpt}
                  onClick={() => setUnit(u)}
                >
                  {t.units[u]}
                </button>
              ))}
            </div>
          </Row>
          <Row label={t.settings.theme} sub="Dark · warm graphite" />
          <Row label={t.lang.label}>
            <div style={styles.langToggle}>
              {(["en", "zh"] as const).map((l) => (
                <button
                  key={l}
                  style={l === locale ? styles.langOptActive : styles.langOpt}
                  onClick={() => setLocale(l)}
                >
                  {t.lang[l]}
                </button>
              ))}
            </div>
          </Row>
        </Section>

        <Section label={t.settings.accountSection}>
          <button style={styles.shareBtn} onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? t.settings.signingOut : t.settings.signOut}
          </button>
          {signOutFailed && <p className="error" role="alert">{t.settings.signOutFailed}</p>}
        </Section>

        <Section label={t.settings.dangerSection}>
          {isCreator ? (
            <button style={styles.danger} onClick={() => setDeleteOpen(true)}>
              {t.settings.deleteBaby}
            </button>
          ) : (
            <button
              style={{ ...styles.danger, color: leaveArmed ? "var(--bh-surface)" : "var(--bh-attn)", background: leaveArmed ? "var(--bh-attn)" : "transparent", textAlign: leaveArmed ? "center" : "left", borderRadius: 14 }}
              onClick={leaveTracker}
            >
              {leaveArmed ? t.settings.leaveArmed : t.settings.leaveTracker}
            </button>
          )}
        </Section>

        <Section label={t.settings.aboutSection}>
          <Row label={t.settings.version} sub="0.5.0 (design rebuild · Phase 1)" />
        </Section>
      </div>

      <DeleteConfirm
        open={deleteOpen}
        babyName={baby.name}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={async () => {
          await onDelete();
          setDeleteOpen(false);
        }}
      />

      <ShareSheet open={shareOpen} babyId={baby.id} babyName={baby.name} onClose={() => setShareOpen(false)} />
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionLabel}>{label}</div>
      <div style={styles.sectionBody}>{children}</div>
    </div>
  );
}

function Row({ label, value, sub, children }: { label: string; value?: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div style={styles.row}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.rowLabel}>{label}</div>
        {value && <div style={styles.rowValue}>{value}</div>}
        {sub && <div style={styles.rowSub}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  head: { display: "flex", alignItems: "center", padding: "8px 14px 14px", gap: 8 },
  back: {
    width: 44,
    height: 44,
    borderRadius: 999,
    background: "var(--bh-surface)",
    border: "none",
    color: "var(--bh-ink-2)",
    cursor: "pointer",
    fontSize: 18,
  },
  title: { flex: 1, textAlign: "center", fontSize: 18, fontWeight: 600, color: "var(--bh-ink)" },
  section: { margin: "0 14px 18px" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 8,
    padding: "0 4px",
  },
  sectionBody: { background: "var(--bh-surface)", borderRadius: 20, padding: "4px 18px" },
  row: {
    display: "flex",
    alignItems: "center",
    padding: "16px 0",
    borderBottom: "1px solid var(--bh-divider)",
    gap: 14,
  },
  rowLabel: { fontSize: 15, color: "var(--bh-ink-3)", marginBottom: 2 },
  rowValue: { fontSize: 16, color: "var(--bh-ink)", fontWeight: 500 },
  rowSub: { fontSize: 13, color: "var(--bh-ink-3)", marginTop: 2 },
  youTag: { fontSize: 13, color: "var(--bh-ink-3)", fontWeight: 400 },
  langToggle: {
    display: "flex",
    gap: 4,
    background: "var(--bh-surface-2)",
    borderRadius: 12,
    padding: 3,
  },
  langOpt: {
    appearance: "none",
    border: "none",
    background: "transparent",
    color: "var(--bh-ink-3)",
    fontSize: 14,
    fontFamily: "inherit",
    padding: "7px 14px",
    borderRadius: 9,
    cursor: "pointer",
  },
  langOptActive: {
    appearance: "none",
    border: "none",
    background: "var(--bh-surface)",
    color: "var(--bh-ink)",
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "inherit",
    padding: "7px 14px",
    borderRadius: 9,
    cursor: "pointer",
  },
  helper: { fontSize: 13, color: "var(--bh-ink-3)", padding: "10px 0", lineHeight: 1.5 },
  nameRow: { display: "flex", gap: 8, padding: "14px 0 0" },
  nameInput: {
    flex: 1,
    appearance: "none",
    background: "var(--bh-surface-2)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 12,
    color: "var(--bh-ink)",
    fontSize: 16,
    fontFamily: "inherit",
    padding: "12px 12px",
    outline: "none",
  },
  saveBtn: {
    appearance: "none",
    background: "var(--bh-ink)",
    color: "var(--bh-surface)",
    border: "none",
    borderRadius: 12,
    padding: "0 18px",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  removeBtn: {
    appearance: "none",
    background: "var(--bh-surface-2)",
    color: "var(--bh-attn)",
    border: "none",
    borderRadius: 999,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  shareBtn: {
    width: "100%",
    appearance: "none",
    background: "transparent",
    border: "none",
    padding: "16px 0",
    fontSize: 16,
    color: "var(--bh-ink)",
    fontWeight: 500,
    cursor: "pointer",
    textAlign: "left",
    minHeight: 56,
  },
  danger: {
    width: "100%",
    appearance: "none",
    background: "transparent",
    border: "none",
    padding: "16px",
    fontSize: 16,
    color: "var(--bh-attn)",
    fontWeight: 600,
    cursor: "pointer",
    textAlign: "left",
    minHeight: 56,
  },
};
